import { useState } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { Load } from "components/load";
import PageError from "components/page-error";
import { PageLayout } from "components/layout";
import { Button } from "components/ui/button";
import { Switch } from "@/components/ui/switch";
import { layoutClasses } from "@/utils/css-classes";

import {
  useCommitAllDepositDraftsMutation,
  useCreateDepositDraftMutation,
  useGetDepositBanksQuery,
  useGetDepositItemsQuery,
  useListDepositDraftsQuery,
} from "@/services/deposit-reconciliation";
import { toast } from "@/components/ui/use-toast";
import type { IApiError } from "src/@types/deposit-reconciliation";

import { currentWeekPeriod, isValidPeriod } from "@/utils/date-range";
import { MESSAGES, TEXTS } from "../utils/messages";
import CommitModal from "./components/commit-modal";
import DraftList from "./components/draft-list";
import StoreHeader from "./components/store-header";
import NsuTable from "./components/nsu-table";
import DepositTable from "./components/deposit-table";
import SumHeader from "./components/sum-header";
import { useSelection } from "./hooks/use-selection";
import { storeLabel } from "./utils/store-label";
import { PAGE_SIZE } from "./constants";
import type { FilterKey, LocalFilters } from "./types";
import {
  bankFilterOptions,
  depositFilterValues,
  depositQueryParams,
  nsuFilterValues,
  nsuQueryParams,
} from "./utils/deposit-filters";

export default function DepositReconciliationPage() {
  const { storeCode } = useParams<{ storeCode: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const periodStart = searchParams.get("periodStart") ?? undefined;
  const periodEnd = searchParams.get("periodEnd") ?? undefined;
  const includeReconciledParam = searchParams.get("includeReconciled") === "true";

  const hasBothPeriods = Boolean(periodStart) && Boolean(periodEnd);

  const buildCurrentWeekPath = (): string => {
    const { periodStart: start, periodEnd: end } = currentWeekPeriod();
    const params = new URLSearchParams();
    params.set("periodStart", start);
    params.set("periodEnd", end);
    if (includeReconciledParam) params.set("includeReconciled", "true");
    return `/conciliacao/depositos/${storeCode}?${params.toString()}`;
  };

  const [filters, setFilters] = useState<LocalFilters>({
    includeReconciled: includeReconciledParam,
  });
  const [nsuPage, setNsuPage] = useState(1);
  const [depositPage, setDepositPage] = useState(1);
  const [pageSizeNsu, setPageSizeNsu] = useState(PAGE_SIZE);
  const [pageSizeDeposit, setPageSizeDeposit] = useState(PAGE_SIZE);

  const { data, isLoading, isFetching, error, refetch: refetchItems } = useGetDepositItemsQuery(
    {
      storeCode: Number(storeCode),
      periodStart,
      periodEnd,
      includeReconciled: filters.includeReconciled,
      ...nsuQueryParams(filters),
      ...depositQueryParams(filters),
      nsuPage,
      depositPage,
      nsuPageSize: pageSizeNsu,
      depositPageSize: pageSizeDeposit,
    },
    // Não dispara com período inválido/ausente (evita 400) — mesma guarda do
    // stores-summary e dos drafts.
    { refetchOnFocus: false, skip: !isValidPeriod(periodStart, periodEnd) },
  );

  const handleNsuPageSizeChange = (size: number) => {
    setPageSizeNsu(size);
    setNsuPage(1);
  };

  const handleDepositPageSizeChange = (size: number) => {
    setPageSizeDeposit(size);
    setDepositPage(1);
  };

  const nsus = data?.nsus.data ?? [];
  const deposits = data?.deposits.data ?? [];

  const sel = useSelection(nsus, deposits);

  const { data: banks } = useGetDepositBanksQuery();

  const handleFilter = (key: FilterKey, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setNsuPage(1);
    setDepositPage(1);
  };

  const toggleReconciled = (checked: boolean) => {
    setFilters(prev => ({ ...prev, includeReconciled: checked }));
    setNsuPage(1);
    setDepositPage(1);
    sel.clear();
  };

  const { data: drafts = [], isFetching: loadingDrafts, refetch: refetchDrafts } =
    useListDepositDraftsQuery(
      {
        storeCode: Number(storeCode),
        periodStart: periodStart ?? "",
        periodEnd: periodEnd ?? "",
        mineOnly: true,
      },
      { skip: !isValidPeriod(periodStart, periodEnd) },
    );

  const [createDraft, { isLoading: savingDraft }] =
    useCreateDepositDraftMutation();

  const [commitOpen, setCommitOpen] = useState(false);
  const [commitAll, { isLoading: committing }] =
    useCommitAllDepositDraftsMutation();

  const handleCommit = async () => {
    if (!periodStart || !periodEnd) return;
    try {
      const res = await commitAll({
        storeCode: Number(storeCode),
        periodStart,
        periodEnd,
      }).unwrap();
      toast(MESSAGES.COMMIT.SUCCESS(res.createdBatchIds.length));
      setCommitOpen(false);
      sel.clear();
    } catch (err) {
      toast(MESSAGES.COMMIT.ERROR(err as IApiError));
    }
  };

  const handleSaveDraft = async () => {
    if (!periodStart || !periodEnd) return;
    try {
      const res = await createDraft({
        storeCode: Number(storeCode),
        periodStart,
        periodEnd,
        nsuIds: [...sel.selectedNsuIds],
        bankDepositIds: [...sel.selectedDepositIds],
      }).unwrap();
      toast(MESSAGES.DRAFT.SAVED(res.draftId));
      sel.clear();
      refetchItems();
      refetchDrafts();
    } catch (err) {
      toast(MESSAGES.DRAFT.SAVE_ERROR(err as IApiError));
    }
  };

  if (!hasBothPeriods)
    return <Navigate to={buildCurrentWeekPath()} replace />;

  if (!isValidPeriod(periodStart, periodEnd))
    return (
      <PageError
        status={400}
        message={TEXTS.RECONCILIATION.INVALID_PERIOD}
        action={
          <Button
            onClick={() => navigate(buildCurrentWeekPath())}
            className="px-6 py-2 h-auto rounded bg-[#F5CB2B] text-[#2B0C1A] font-semibold hover:bg-[#e6bc1a] transition"
          >
            {TEXTS.RECONCILIATION.BACK_TO_CURRENT_WEEK}
          </Button>
        }
      />
    );
  if (error)
    return (
      <PageError
        status={(error as IApiError)?.status ?? 500}
        message={TEXTS.COMMON.INTERNAL_SERVER_ERROR}
      />
    );
  if (isLoading) return <Load />;
  if (!data) return null;

  return (
    <PageLayout>
      <div className={layoutClasses.pageContainer}>
        <StoreHeader
          storeHeader={data.storeHeader}
          periodStart={periodStart}
          periodEnd={periodEnd}
          includeReconciled={filters.includeReconciled}
          hasUnsavedSelection={sel.hasAnySelection}
        />

        <SumHeader
          sumNsu={sel.sumNsu}
          sumDeposit={sel.sumDeposit}
          diff={sel.diff}
          isBalanced={sel.isBalanced}
          hasAnySelection={sel.hasAnySelection}
          nsuCount={sel.selectedNsuIds.size}
          depositCount={sel.selectedDepositIds.size}
        />

        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            {sel.hasAnySelection && (
              <Button
                variant="outline"
                size="sm"
                onClick={sel.clear}
                className="text-xs"
              >
                {TEXTS.RECONCILIATION.CLEAR_SELECTION}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              {TEXTS.RECONCILIATION.SHOW_RECONCILED}
              <Switch
                checked={filters.includeReconciled}
                onCheckedChange={toggleReconciled}
              />
            </label>
            <Button
              size="sm"
              variant="outline"
              disabled={!sel.isBalanced || savingDraft}
              onClick={handleSaveDraft}
              className="border-[#2B0C1A] text-[#2B0C1A] hover:bg-[#2B0C1A] hover:text-white disabled:opacity-50"
            >
              {savingDraft
                ? TEXTS.RECONCILIATION.SAVING_DRAFT
                : TEXTS.RECONCILIATION.SAVE_DRAFT}
            </Button>
            <Button
              size="sm"
              disabled={drafts.length === 0 || committing}
              onClick={() => setCommitOpen(true)}
              className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {TEXTS.RECONCILIATION.COMMIT}
              {drafts.length > 0 ? ` (${drafts.length})` : ""}
            </Button>
          </div>
        </div>

        {drafts.length > 0 && (
          <div className="mb-4 border border-yellow-200 bg-yellow-50/30 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-yellow-900">
                {TEXTS.RECONCILIATION.PENDING_DRAFTS(drafts.length)}
              </span>
              <span className="text-xs text-yellow-700">
                {TEXTS.RECONCILIATION.DRAFT_DETAILS_HINT}
              </span>
            </div>
            <DraftList
              drafts={drafts}
              isLoading={loadingDrafts}
              onDiscarded={() => {
                refetchItems();
                refetchDrafts();
              }}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-4">
          <NsuTable
            data={nsus}
            page={nsuPage}
            pageSize={pageSizeNsu}
            total={data.nsus.total}
            isFetching={isFetching}
            filters={nsuFilterValues(filters)}
            onFilterChange={handleFilter}
            onPageChange={setNsuPage}
            onPageSizeChange={handleNsuPageSizeChange}
            selectedIds={sel.selectedNsuIds}
            onToggle={sel.toggleNsu}
            onToggleAll={sel.toggleNsuAll}
            periodStart={periodStart}
            periodEnd={periodEnd}
          />
          <DepositTable
            data={deposits}
            page={depositPage}
            pageSize={pageSizeDeposit}
            total={data.deposits.total}
            isFetching={isFetching}
            filters={depositFilterValues(filters)}
            bankOptions={bankFilterOptions(banks)}
            periodStart={periodStart}
            periodEnd={periodEnd}
            onFilterChange={handleFilter}
            onPageChange={setDepositPage}
            onPageSizeChange={handleDepositPageSizeChange}
            selectedIds={sel.selectedDepositIds}
            onToggle={sel.toggleDeposit}
            onToggleAll={sel.toggleDepositAll}
          />
        </div>
      </div>

      <CommitModal
        open={commitOpen}
        drafts={drafts}
        storeLabel={storeLabel(data.storeHeader)}
        isLoading={committing}
        onCancel={() => setCommitOpen(false)}
        onConfirm={handleCommit}
      />
    </PageLayout>
  );
}
